import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { Box, Col, Row } from '@opendoor/atoms/box';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconArrowRight } from '@tabler/icons-react';
import type { EntryComponent } from 'cms/entries/entries';
import type {
  ILpComponentHeaderWithId,
  ILpComponentMedia,
  ILpComponentStandaloneText,
} from 'declarations/contentful';
import { optimizeContentfulImageUrl } from 'helpers/imageUrl';

import Container from './shared/Container';
import { Subhead, SubheadSmall, TitleWithAccent } from './shared/Typography';

type TextAlign = 'left' | 'center';

const alignClasses = (base?: TextAlign, largerThanSM?: TextAlign): string =>
  cn(
    base === 'center' && 'text-center',
    base === 'left' && 'text-left',
    largerThanSM === 'center' && 'md:text-center',
    largerThanSM === 'left' && 'md:text-left',
  );

// `bodyPGap` is a Contentful-authored px gap (default 64); the rendered body row gap is
// half that. Map the authored value to a static Tailwind class so spacing stays on the
// scale (Atoms primitives take `className`, not a dynamic `style`). In practice content
// only uses 64 and 32; the rest cover plausible authored values, defaulting to `gap-8`.
const BODY_GAP_CLASS: Record<number, string> = {
  16: 'gap-2',
  24: 'gap-3',
  32: 'gap-4',
  48: 'gap-6',
  64: 'gap-8',
  96: 'gap-12',
  128: 'gap-16',
};

interface BodyParserConfig {
  textAlign?: TextAlign;
  largerThanSMTextAlign?: TextAlign;
}

const bodyParser = ({ textAlign, largerThanSMTextAlign }: BodyParserConfig): Options => {
  const align = alignClasses(textAlign, largerThanSMTextAlign);
  return {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => (
        <Text as="p" className={cn('text-body-lg', align)}>
          {children}
        </Text>
      ),
      [BLOCKS.QUOTE]: (node, _) => {
        const text = node.content
          .flatMap((p) => ('content' in p ? p.content : []))
          .map((t) => ('value' in t ? t.value : ''))
          .join('');
        return (
          // pl-8 indents the quote on mobile so the hanging quotation mark stays on-screen;
          // on desktop it sits flush and the mark hangs into the gutter.
          // eslint-disable-next-line @opendoor/no-html-elements -- Atoms has no blockquote primitive; raw element preserves quote semantics
          <blockquote className="flex flex-row pl-8 md:pl-0">
            <Text as="p" className={cn('relative text-header-md', align)}>
              {/* right-full + pr-4 hangs the mark one spacing step (16px) left of the text */}
              <Text as="span" aria-hidden className="absolute right-full pr-4 text-header-md">
                “
              </Text>
              {text}
            </Text>
          </blockquote>
        );
      },
      [BLOCKS.HEADING_1]: (_, children) => (
        <Text as="h1" className={cn('text-subheader-lg', align)}>
          {children}
        </Text>
      ),
      [BLOCKS.HEADING_2]: (_, children) => (
        <Text as="h2" className={cn('text-subheader-lg', align)}>
          {children}
        </Text>
      ),
      [BLOCKS.HEADING_3]: (_, children) => (
        <Text as="h3" className={cn('text-subheader-md', align)}>
          {children}
        </Text>
      ),
      [BLOCKS.HEADING_4]: (_, children) => (
        <Text as="h4" className={cn('text-subheader-sm', align)}>
          {children}
        </Text>
      ),
      [BLOCKS.HEADING_5]: (_, children) => (
        <Text as="h5" className={cn('text-body-lg', align)}>
          {children}
        </Text>
      ),
      [BLOCKS.HEADING_6]: (_, children) => (
        <Text as="h6" className={cn('text-body-md', align)}>
          {children}
        </Text>
      ),
      [BLOCKS.UL_LIST]: (_, children) => (
        // eslint-disable-next-line @opendoor/no-html-elements -- Atoms has no list primitive; raw element preserves list semantics
        <ul className="flex flex-col">{children}</ul>
      ),
      [BLOCKS.OL_LIST]: (_, children) => (
        // eslint-disable-next-line @opendoor/no-html-elements -- Atoms has no list primitive; raw element preserves list semantics
        <ol className="flex flex-col">{children}</ol>
      ),
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const target: ILpComponentMedia | ILpComponentHeaderWithId = node.data.target;
        if (target.sys.contentType.sys.id === 'lpComponentMedia') {
          const { fields } = target as ILpComponentMedia;
          return (
            <Col className="scroll-mt-20">
              {fields.image && (
                <Image
                  src={
                    fields.image.fields?.file?.url
                      ? optimizeContentfulImageUrl(fields.image.fields.file.url, {
                          width: 1600,
                          quality: 80,
                        })
                      : ''
                  }
                  alt={fields.image.fields?.description || fields.image.fields?.title || ''}
                  className="rounded-2xl"
                />
              )}
            </Col>
          );
        }
        const { fields } = target as ILpComponentHeaderWithId;
        return (
          <Col id={fields.elementID} className="scroll-mt-20">
            {fields.title && documentToReactComponents(fields.title)}
          </Col>
        );
      },
    },
  };
};

const introOptions: Options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <Text as="p" className="text-body-lg">
        {children}
      </Text>
    ),
  },
};

const sidebarParse: Options = {
  renderNode: {
    [BLOCKS.HEADING_2]: (_, children) => (
      <Text as="h2" className="pb-6 text-header-md font-medium text-od-content-secondary">
        {children}
      </Text>
    ),
    [BLOCKS.UL_LIST]: (_, children) => (
      // role="list" is intentional: `list-none` strips the implicit list role in
      // Safari/VoiceOver, so it must be restored for assistive tech.
      // eslint-disable-next-line @opendoor/no-html-elements, jsx-a11y/no-redundant-roles -- Atoms has no list primitive; raw element preserves list semantics
      <ul role="list" className="flex list-none flex-col gap-6 p-0 pb-6">
        {children}
      </ul>
    ),
    [BLOCKS.LIST_ITEM]: (_, children) => <Text as="li">{children}</Text>,
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <Text as="p" className="text-body-md text-od-content-secondary">
        {children}
      </Text>
    ),
    [INLINES.HYPERLINK]: (node, children) => {
      const label = node.content
        .map((child) => ('value' in child ? child.value : ''))
        .join('')
        .trim();
      return (
        <Box
          href={node.data.uri}
          aria-label={label || node.data.uri}
          analyticsName="cosmos-landing-page-standalone-text-sidebar"
          className="block pb-2 font-medium text-od-content-secondary no-underline"
        >
          {children}
        </Box>
      );
    },
  },
};

// dialogId is for instances where the Standalone text is in a Sheet
const Header = ({
  entry,
  dialogId,
  centered,
}: {
  entry: ILpComponentStandaloneText;
  dialogId?: string;
  centered?: boolean;
}) => {
  const { fields } = entry;
  const centeredTextProps = centered ? ({ $largerThanSM: { textAlign: 'center' } } as const) : {};

  return (
    <Col as="header" className="pb-4 md:pb-6">
      {fields.title && (
        <TitleWithAccent
          overRideTitleTag="h1"
          title={fields.title}
          titleAccent={fields.titleAccent}
          titleSize={fields.titleSize}
          marginHorizontal={fields.layout === 'Centered' ? 'auto' : undefined}
          {...centeredTextProps}
          id={dialogId || undefined}
        />
      )}
      {fields.subhead &&
        (fields.titleSize === 'Large' ? (
          <Subhead {...centeredTextProps}>{fields.subhead}</Subhead>
        ) : (
          <SubheadSmall {...centeredTextProps}>{fields.subhead}</SubheadSmall>
        ))}
    </Col>
  );
};

const Cta = ({ entry }: { entry: ILpComponentStandaloneText }) => {
  const { fields } = entry;

  return (
    <Row
      href={fields.ctaUrl ?? ''}
      aria-label={fields.ctaDisplayText || ''}
      analyticsName={`cosmos-landing-page-standalone-text-${fields?.analyticsId}`}
      className="inline-flex items-center gap-2 pt-6 text-od-content-accent-brand md:pt-8"
    >
      <IconArrowRight size={15} aria-hidden="true" />
      {/* relative -top-px restores the original 1px optical nudge against the icon */}
      <Text as="p" className="relative -top-px whitespace-nowrap text-body-md font-medium">
        {fields.ctaDisplayText}
      </Text>
    </Row>
  );
};

const Intro = ({
  document,
}: {
  document: NonNullable<ILpComponentStandaloneText['fields']['intro']>;
}) => (
  <Col className="gap-8 pb-8 md:pb-16">{documentToReactComponents(document, introOptions)}</Col>
);

const SidebarLayout = ({ entry }: { entry: ILpComponentStandaloneText }) => {
  const { fields } = entry;
  const resolvedGap = fields.bodyPGap ?? 64;
  const bodyGapClass = BODY_GAP_CLASS[resolvedGap] ?? 'gap-8';

  return (
    <>
      <Col>
        {(fields.title || fields.subhead) && <Header entry={entry} />}
        {fields.intro && <Intro document={fields.intro} />}
        {fields.body && (
          <Col className={cn('pb-4 md:pb-0', bodyGapClass)}>
            {documentToReactComponents(fields.body, bodyParser({}))}
          </Col>
        )}
        {fields.sidebar && (
          <Col className="gap-6">{documentToReactComponents(fields.sidebar, sidebarParse)}</Col>
        )}
      </Col>
      {fields.ctaDisplayText && fields.ctaUrl && <Cta entry={entry} />}
    </>
  );
};

// Centered Layout is also used by Sheets
// dialogId is for instances where the Standalone text is in a Sheet
export const CenteredLayout = ({
  entry,
  dialogId,
}: {
  entry: ILpComponentStandaloneText;
  dialogId?: string;
}) => {
  const { fields } = entry;
  const resolvedGap = fields.bodyPGap ?? 64;
  const bodyGapClass = BODY_GAP_CLASS[resolvedGap] ?? 'gap-8';

  return (
    <Col>
      {(fields.title || fields.subhead) && <Header entry={entry} dialogId={dialogId} centered />}
      {(fields.intro || fields.body) && (
        <Col className={bodyGapClass}>
          {fields.intro && <Intro document={fields.intro} />}
          {fields.body &&
            documentToReactComponents(fields.body, bodyParser({ largerThanSMTextAlign: 'center' }))}
        </Col>
      )}
      {fields.ctaDisplayText && fields.ctaUrl && (
        <Col className="md:items-center">
          <Cta entry={entry} />
        </Col>
      )}
    </Col>
  );
};

const DefaultLayout = ({ entry }: { entry: ILpComponentStandaloneText }) => {
  const { fields } = entry;
  const resolvedGap = fields.bodyPGap ?? 64;
  const bodyGapClass = BODY_GAP_CLASS[resolvedGap] ?? 'gap-8';

  return (
    <>
      {(fields.title || fields.subhead) && <Header entry={entry} />}

      {fields.intro && <Intro document={fields.intro} />}
      {fields.body && (
        <Col className={bodyGapClass}>{documentToReactComponents(fields.body, bodyParser({}))}</Col>
      )}
      {fields.ctaDisplayText && fields.ctaUrl && <Cta entry={entry} />}
    </>
  );
};

const RenderStandaloneText = (entry: ILpComponentStandaloneText) => {
  const { fields } = entry;

  return (
    <Container className="pb-8 md:pb-10">
      {/* eslint-disable-next-line no-nested-ternary -- Grandfathered violation */}
      {fields.layout === 'Sidebar' ? (
        <SidebarLayout entry={entry} />
      ) : fields.layout === 'Centered' ? (
        <CenteredLayout entry={entry} />
      ) : (
        <DefaultLayout entry={entry} />
      )}
    </Container>
  );
};

const StandaloneText: EntryComponent<ILpComponentStandaloneText> = {
  render: RenderStandaloneText,
};

export default StandaloneText;
